import { useCallback, useEffect, useRef, useState } from "react";
import { Header } from "./components/Header";
import { HeroLogo } from "./components/HeroLogo";
import { Tagline } from "./components/Tagline";
import { MarketingCopy } from "./components/MarketingCopy";
import { TrustCard } from "./components/TrustCard";
import { PlanCard } from "./components/PlanCard";
import { ExistingUser } from "./components/ExistingUser";
import { EmailDialog } from "./components/EmailDialog";
import { useTeletextKeyboard } from "./hooks/useTeletextKeyboard";
import { createCheckoutSession, requestPortalLink, type Plan } from "./lib/api";

const MONTHLY_FEATURES = [
  { text: "Full access" },
  { text: "Cancel anytime" },
  { text: "No long-term commitment" },
];

const QUARTERLY_FEATURES = [
  { text: "Full access" },
  { text: "Save over 9%", emphasized: true },
  { text: "Billed every 3 months" },
];

type PendingPlan = Plan | null;

export default function App() {
  const [pendingCheckout, setPendingCheckout] = useState<PendingPlan>(null);
  const [emailDialogOpen, setEmailDialogOpen] = useState(false);
  const [dialogMode, setDialogMode] = useState<"portal" | "checkout">("portal");
  const [dialogLoading, setDialogLoading] = useState(false);
  const [dialogSent, setDialogSent] = useState(false);
  const [dialogSentMessage, setDialogSentMessage] = useState<
    string | undefined
  >();
  const [dialogError, setDialogError] = useState<string | undefined>();
  const [errors, setErrors] = useState<{
    monthly?: string;
    quarterly?: string;
  }>({});

  const monthlyButtonRef = useRef<HTMLDivElement>(null);
  const quarterlyButtonRef = useRef<HTMLDivElement>(null);
  const manageButtonRef = useRef<HTMLButtonElement>(null);

  // Selecting a plan no longer goes straight to Stripe — we collect the email
  // first so the backend can reuse an existing customer (preventing duplicate
  // Stripe Customers) and redirect already-subscribed users to MANAGE instead.
  const startCheckout = useCallback((plan: Plan) => {
    setErrors((e) => ({ ...e, [plan]: undefined }));
    setPendingCheckout(plan);
    setDialogMode("checkout");
    setDialogError(undefined);
    setDialogSent(false);
    setDialogSentMessage(undefined);
    setEmailDialogOpen(true);
  }, []);

  const openPortal = useCallback(() => {
    setPendingCheckout(null);
    setDialogMode("portal");
    setDialogError(undefined);
    setDialogSent(false);
    setDialogSentMessage(undefined);
    setEmailDialogOpen(true);
  }, []);

  const handleEmailSubmit = useCallback(
    async (email: string) => {
      setDialogError(undefined);
      setDialogLoading(true);
      try {
        if (dialogMode === "checkout" && pendingCheckout) {
          const res = await createCheckoutSession(pendingCheckout, email);
          if ("url" in res) {
            window.location.assign(res.url);
            return; // navigating away; keep the loading state until we leave
          }
          // Already subscribed: switch to MANAGE and email a portal link so
          // they don't open a second subscription on the same account.
          await requestPortalLink(email);
          setDialogMode("portal");
          setDialogSentMessage(
            "You already have an active subscription. We've emailed you a one-time link to manage it.",
          );
          setDialogSent(true);
        } else {
          await requestPortalLink(email);
          setDialogSent(true);
        }
      } catch (err) {
        const fallback =
          dialogMode === "checkout"
            ? "Checkout unavailable. Try again."
            : "Couldn't send the link. Try again.";
        setDialogError(err instanceof Error ? err.message : fallback);
      } finally {
        setDialogLoading(false);
      }
    },
    [dialogMode, pendingCheckout],
  );

  const handleEmailCancel = useCallback(() => {
    setEmailDialogOpen(false);
    setDialogLoading(false);
    setDialogSent(false);
    setDialogSentMessage(undefined);
    setDialogError(undefined);
    setPendingCheckout(null);
  }, []);

  // When the backend redirects here after a magic-link failure
  // (?portal=expired or ?portal=error), open the email dialog with the
  // matching themed error message instead of rendering raw JSON in the
  // browser. Strip the query param afterwards so refreshing doesn't
  // re-trigger it.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const portal = params.get("portal");
    if (portal !== "expired" && portal !== "error") return;

    const message =
      portal === "expired"
        ? "This link has expired or already been used. Request a new one."
        : "Couldn't open the billing portal. Try again.";

    setDialogError(message);
    setDialogSent(false);
    setEmailDialogOpen(true);

    params.delete("portal");
    const cleaned =
      window.location.pathname +
      (params.toString() ? `?${params}` : "") +
      window.location.hash;
    window.history.replaceState(null, "", cleaned);
  }, []);

  const focusEl = (el: HTMLElement | null) => {
    if (!el) return;
    el.scrollIntoView({ behavior: "smooth", block: "center" });
    el.focus({ preventScroll: true });
  };

  useTeletextKeyboard({
    "0": () => window.scrollTo({ top: 0, behavior: "smooth" }),
    "100": () => focusEl(document.getElementById("home")),
    "101": () => {
      const btn = monthlyButtonRef.current?.querySelector<HTMLButtonElement>("button.plan-card__cta");
      focusEl(btn ?? null);
    },
    "102": () => {
      const btn = quarterlyButtonRef.current?.querySelector<HTMLButtonElement>("button.plan-card__cta");
      focusEl(btn ?? null);
    },
    "200": () => focusEl(manageButtonRef.current),
    "300": () => focusEl(document.getElementById("help")),
  });

  return (
    <div className="page scanlines">
      <div className="page__frame">
        <Header />
        <hr className="page__rule" />

        <HeroLogo />
        <hr className="page__rule" />

        <Tagline />

        <main className="page__main">
          <div className="marketing">
            <MarketingCopy />
            <TrustCard />
          </div>

          <PlanCard
            ref={monthlyButtonRef}
            id="monthlyPlan"
            variant="monthly"
            pageCode="101"
            title="MONTHLY"
            price="$55"
            priceSubtext="per month"
            features={MONTHLY_FEATURES}
            ctaLabel="GET STARTED"
            ctaAriaLabel="Start monthly subscription checkout, fifty-five dollars per month"
            onSelect={() => startCheckout("monthly")}
            isLoading={pendingCheckout === "monthly" && dialogLoading}
            isDisabled={pendingCheckout !== null && pendingCheckout !== "monthly"}
            errorMessage={errors.monthly}
          />

          <PlanCard
            ref={quarterlyButtonRef}
            id="quarterlyPlan"
            variant="quarterly"
            pageCode="102"
            title="QUARTERLY"
            price="$50"
            priceSubtext="per month"
            features={QUARTERLY_FEATURES}
            badge="BEST VALUE"
            ctaLabel="GET STARTED"
            ctaAriaLabel="Start quarterly subscription checkout, fifty dollars per month, billed every three months"
            onSelect={() => startCheckout("quarterly")}
            isLoading={pendingCheckout === "quarterly" && dialogLoading}
            isDisabled={pendingCheckout !== null && pendingCheckout !== "quarterly"}
            errorMessage={errors.quarterly}
          />
        </main>

        <hr className="page__rule" id="help" />

        <ExistingUser
          onManage={openPortal}
          manageButtonRef={manageButtonRef}
        />
      </div>

      <EmailDialog
        open={emailDialogOpen}
        mode={dialogMode}
        onSubmit={handleEmailSubmit}
        onCancel={handleEmailCancel}
        errorMessage={dialogError}
        isLoading={dialogLoading}
        isSent={dialogSent}
        sentMessage={dialogSentMessage}
      />
    </div>
  );
}
